import { useCallback, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import type { CoverFrame } from "shared/types/media";
import { FIELDS } from "constants/PostFormFields";
import { useTrackEvent } from "libs/analytics";
import { COVER_IMAGE_SELECTED } from "constants/Events";
import {
  VIDEO_COVER_TYPES,
  VIDEO_COVER_TIMESTAMPS,
} from "constants/VideoCoverConstants";
import {
  getOrCreateVideoBlob,
  getVideoPreviewImageWithRetry,
  revokeBlobUrl,
} from "../utils";
import type { LazyBlobRef } from "../types";
import { logVideoCoverBreadcrumb, withVideoCoverSpan } from "../observability";
import { resolveMediaCorrelationKey } from "utils/media-observability";
import type { FormPlatformType } from "shared/types/post";

interface Platform {
  TYPE: string;
  NAME: string;
}

interface UseVideoCoverOperationsProps {
  updateFields: (
    fields: Record<string, unknown>,
    targetPlatform?: FormPlatformType
  ) => void;
  platforms: Platform[];
  originalMediaUrl?: string;
  lowQualityMediaUrl?: string;
  videoUrl?: string;
  thumbnailUrl?: string;
  videoCoverImageTimestamp?: number;
  videoCoverPreview?: string;
  setCover: (cover: string) => void;
  setIsLoadingThumb: (loading: boolean) => void;
  setShowLowResFallbackWarning: (show: boolean) => void;
  setOriginalValues: (values: {
    timestamp?: number;
    coverType?: string;
  }) => void;
  platformFullname: string;
  coverFrames?: CoverFrame[];
  lazyBlobRef?: LazyBlobRef;
  coverSessionKey: string;
  selectedPlatform: FormPlatformType;
}

export function useVideoCoverOperations({
  updateFields,
  platforms,
  originalMediaUrl,
  lowQualityMediaUrl,
  videoUrl,
  thumbnailUrl,
  videoCoverImageTimestamp,
  videoCoverPreview,
  setCover,
  setIsLoadingThumb,
  setShowLowResFallbackWarning,
  setOriginalValues,
  platformFullname,
  coverFrames,
  lazyBlobRef,
  coverSessionKey,
  selectedPlatform,
}: UseVideoCoverOperationsProps) {
  const trackEvent = useTrackEvent();
  const operationIdRef = useRef(0);
  const coverSessionKeyRef = useRef(coverSessionKey);

  if (coverSessionKeyRef.current !== coverSessionKey) {
    coverSessionKeyRef.current = coverSessionKey;
    operationIdRef.current += 1;
  }

  useEffect(
    () => () => {
      operationIdRef.current += 1;
    },
    []
  );

  // Refs for values read by generateOriginalCover that should NOT
  // cause it to be recreated (which would retrigger the calling useEffect).
  const videoCoverPreviewRef = useRef(videoCoverPreview);
  videoCoverPreviewRef.current = videoCoverPreview;
  const thumbnailUrlRef = useRef(thumbnailUrl);
  thumbnailUrlRef.current = thumbnailUrl;
  const videoCoverImageTimestampRef = useRef(videoCoverImageTimestamp);
  videoCoverImageTimestampRef.current = videoCoverImageTimestamp;

  const onCoverChanged = useCallback(
    async (
      coverThumbnailUrl: string,
      timestampInMs: number,
      message?: string
    ) => {
      const observabilityMetadata = {
        selectedPlatform: platformFullname,
        platformTypes: platforms.map((platform) => platform.TYPE).join(","),
        originalMediaUrl,
        lowQualityMediaUrl,
        videoUrl,
        timestampInMs,
        hasCoverFramesPath: Boolean(coverFrames?.length),
        mediaCorrelationKey: resolveMediaCorrelationKey({
          urls: [originalMediaUrl, lowQualityMediaUrl, videoUrl, thumbnailUrl],
        }),
      };

      logVideoCoverBreadcrumb(
        "video_cover.select_cover.start",
        observabilityMetadata
      );

      const operationId = ++operationIdRef.current;
      let highQualityThumb = coverThumbnailUrl;
      let resolvedSource: string | null = null;

      // Ordered fallback: original video → blob (lazy, CORS-safe) → low-res remote URL
      // When coverFrames are active, videoUrl is a remote URL (not a blob).
      // The lazy blob is created on demand only if the primary remote extraction fails,
      // preserving the same-origin CORS-safe fallback without the upfront download cost.
      const hasCoverFramesPath = Boolean(coverFrames?.length);
      const extractionSources = [
        originalMediaUrl,
        // For legacy path (no coverFrames), videoUrl is already a blob — include it directly.
        // For coverFrames path, skip videoUrl here — lazy blob is tried separately below.
        ...(hasCoverFramesPath ? [] : [videoUrl]),
        lowQualityMediaUrl,
      ].filter(Boolean) as string[];

      if (extractionSources.length > 0 && timestampInMs >= 0) {
        setIsLoadingThumb(true);
        try {
          await withVideoCoverSpan(
            "video_cover.select_cover",
            observabilityMetadata,
            async () => {
              for (const source of extractionSources) {
                try {
                  logVideoCoverBreadcrumb(
                    "video_cover.select_cover.source_attempt",
                    {
                      ...observabilityMetadata,
                      source,
                    }
                  );
                  highQualityThumb = await getVideoPreviewImageWithRetry(
                    source,
                    timestampInMs / 1000
                  );
                  resolvedSource = source;
                  logVideoCoverBreadcrumb(
                    "video_cover.select_cover.source_success",
                    {
                      ...observabilityMetadata,
                      source,
                    }
                  );
                  break;
                } catch (error) {
                  logVideoCoverBreadcrumb(
                    "video_cover.select_cover.source_failure",
                    {
                      ...observabilityMetadata,
                      source,
                      errorMessage:
                        error instanceof Error ? error.message : String(error),
                    }
                  );
                  if (
                    hasCoverFramesPath &&
                    source === originalMediaUrl &&
                    lazyBlobRef
                  ) {
                    const mediaUrl =
                      lowQualityMediaUrl || originalMediaUrl || "";
                    try {
                      logVideoCoverBreadcrumb(
                        "video_cover.select_cover.lazy_blob_fallback.start",
                        {
                          ...observabilityMetadata,
                          mediaUrl,
                        }
                      );
                      const { blobUrl } = await withVideoCoverSpan(
                        "video_cover.lazy_blob_fallback",
                        {
                          ...observabilityMetadata,
                          mediaUrl,
                        },
                        async () => getOrCreateVideoBlob(mediaUrl, lazyBlobRef)
                      );
                      highQualityThumb = await getVideoPreviewImageWithRetry(
                        blobUrl,
                        timestampInMs / 1000
                      );
                      resolvedSource = blobUrl;
                      logVideoCoverBreadcrumb(
                        "video_cover.select_cover.lazy_blob_fallback.success",
                        {
                          ...observabilityMetadata,
                          mediaUrl,
                        }
                      );
                      break;
                    } catch (lazyBlobError) {
                      logVideoCoverBreadcrumb(
                        "video_cover.select_cover.lazy_blob_fallback.failure",
                        {
                          ...observabilityMetadata,
                          mediaUrl,
                          errorMessage:
                            lazyBlobError instanceof Error
                              ? lazyBlobError.message
                              : String(lazyBlobError),
                        }
                      );
                    }
                  }
                }
              }

              if (highQualityThumb === coverThumbnailUrl) {
                logVideoCoverBreadcrumb(
                  "video_cover.select_cover.all_sources_failed",
                  {
                    ...observabilityMetadata,
                    extractionSources: extractionSources.join(","),
                  }
                );
                throw new Error(
                  `Cover frame extraction failed for all ${extractionSources.length} sources`
                );
              }
            }
          );
        } catch (error) {
          if (operationId !== operationIdRef.current) {
            return;
          }

          console.error(
            "Failed to extract video frame from all sources, using thumbnail",
            error
          );
        } finally {
          if (operationId === operationIdRef.current) {
            setIsLoadingThumb(false);
          }
        }
      }

      // Discard stale result if a newer operation started
      if (operationId !== operationIdRef.current) {
        revokeBlobUrl(highQualityThumb);
        if (coverThumbnailUrl !== highQualityThumb) {
          revokeBlobUrl(coverThumbnailUrl);
        }
        return;
      }

      // Revoke the FrameSelector's blob URL if it differs from the final result
      if (coverThumbnailUrl !== highQualityThumb) {
        revokeBlobUrl(coverThumbnailUrl);
      }

      setCover(highQualityThumb);
      updateFields(
        {
          [FIELDS.VIDEO_COVER_PREVIEW]: highQualityThumb,
          [FIELDS.VIDEO_COVER_IMAGE_TIMESTAMP]: timestampInMs,
          [FIELDS.VIDEO_COVER_TYPE]: VIDEO_COVER_TYPES.FRAME_SELECTED,
          [FIELDS.VIDEO_COVER_PREVIEW_SOURCE]: undefined,
        },
        selectedPlatform
      );
      setShowLowResFallbackWarning(
        Boolean(originalMediaUrl) && resolvedSource !== originalMediaUrl
      );
      logVideoCoverBreadcrumb("video_cover.select_cover.finalized", {
        ...observabilityMetadata,
        resolvedSource,
        usedLowResFallback:
          Boolean(originalMediaUrl) && resolvedSource !== originalMediaUrl,
      });

      if (message) {
        toast.success(message);
      }
      trackEvent({
        eventName: COVER_IMAGE_SELECTED,
        metadata: {
          platforms,
          selected: "Frame",
        },
        useServices: ["AMP"],
      });
    },
    [
      videoUrl,
      lowQualityMediaUrl,
      originalMediaUrl,
      coverFrames,
      lazyBlobRef,
      setCover,
      setIsLoadingThumb,
      setShowLowResFallbackWarning,
      updateFields,
      trackEvent,
      platforms,
      platformFullname,
      selectedPlatform,
    ]
  );

  const onCoverUploaded = useCallback(
    (url: string, thumbnailUrl: string) => {
      const persistedCoverUrl = thumbnailUrl || url;

      setCover(persistedCoverUrl);
      setShowLowResFallbackWarning(false);

      updateFields(
        {
          [FIELDS.VIDEO_COVER_PREVIEW]: url,
          [FIELDS.THUMBNAILURL]: persistedCoverUrl,
          [FIELDS.VIDEO_COVER_IMAGE_TIMESTAMP]:
            VIDEO_COVER_TIMESTAMPS.UPLOADED_COVER,
          [FIELDS.VIDEO_COVER_TYPE]: VIDEO_COVER_TYPES.UPLOADED,
          [FIELDS.VIDEO_COVER_PREVIEW_SOURCE]: "media_library",
        },
        selectedPlatform
      );
      trackEvent({
        eventName: COVER_IMAGE_SELECTED,
        metadata: {
          platforms,
          selected: "Upload",
        },
        useServices: ["AMP"],
      });
    },
    [
      setCover,
      setShowLowResFallbackWarning,
      updateFields,
      trackEvent,
      platforms,
      selectedPlatform,
    ]
  );

  const onRevertToOriginal = useCallback(
    (
      originalCover: string,
      originalTimestamp?: number,
      originalCoverType?: string,
      originalThumbnailUrl?: string
    ) => {
      const revertTimestamp = originalTimestamp ?? 0;
      const revertType =
        originalCoverType ?? VIDEO_COVER_TYPES.PLATFORM_DEFAULT;

      setCover(originalCover);
      setShowLowResFallbackWarning(false);
      updateFields(
        {
          [FIELDS.VIDEO_COVER_PREVIEW]: originalCover,
          [FIELDS.THUMBNAILURL]: originalThumbnailUrl ?? originalCover,
          [FIELDS.VIDEO_COVER_IMAGE_TIMESTAMP]: revertTimestamp,
          [FIELDS.VIDEO_COVER_TYPE]: revertType,
          [FIELDS.VIDEO_COVER_PREVIEW_SOURCE]: undefined,
        },
        selectedPlatform
      );

      toast.success(
        platformFullname
          ? `The ${platformFullname} video cover has been reverted to original.`
          : "The video cover has been reverted to original."
      );

      trackEvent({
        eventName: COVER_IMAGE_SELECTED,
        metadata: {
          platforms,
          selected: "Revert",
        },
        useServices: ["AMP"],
      });
    },
    [
      setCover,
      setShowLowResFallbackWarning,
      updateFields,
      platformFullname,
      trackEvent,
      platforms,
      selectedPlatform,
    ]
  );

  const generateOriginalCover = useCallback(async () => {
    if (!videoUrl) return;
    const operationId = ++operationIdRef.current;
    const observabilityMetadata = {
      videoUrl,
      thumbnailUrl: thumbnailUrlRef.current,
      videoCoverImageTimestamp: videoCoverImageTimestampRef.current,
      selectedPlatform: platformFullname,
      mediaCorrelationKey: resolveMediaCorrelationKey({
        urls: [
          originalMediaUrl,
          lowQualityMediaUrl,
          videoUrl,
          thumbnailUrlRef.current,
        ],
      }),
    };

    // Read from refs to avoid recreating this callback when these values change.
    // generateOriginalCover only runs once per cover-panel open, so stale
    // closures are not a concern — the refs always hold the latest value.
    const currentVideoCoverPreview = videoCoverPreviewRef.current;
    const currentThumbnailUrl = thumbnailUrlRef.current;
    const currentTimestamp = videoCoverImageTimestampRef.current;

    setIsLoadingThumb(true);
    try {
      logVideoCoverBreadcrumb(
        "video_cover.generate_original_cover.start",
        observabilityMetadata
      );
      const thumb = await withVideoCoverSpan(
        "video_cover.generate_original_cover",
        observabilityMetadata,
        async () => {
          if (currentVideoCoverPreview) {
            return currentVideoCoverPreview;
          }
          if (
            currentThumbnailUrl &&
            currentTimestamp === VIDEO_COVER_TIMESTAMPS.UPLOADED_COVER
          ) {
            return currentThumbnailUrl;
          }
          if (currentThumbnailUrl) {
            return currentThumbnailUrl;
          }
          return getVideoPreviewImageWithRetry(
            videoUrl,
            (currentTimestamp || 0) / 1000
          );
        }
      );

      // Discard stale result if a newer operation started
      if (operationId !== operationIdRef.current) {
        if (
          thumb !== currentVideoCoverPreview &&
          thumb !== currentThumbnailUrl
        ) {
          revokeBlobUrl(thumb);
        }
        return;
      }

      setCover(thumb);

      // Set appropriate cover type based on source
      let coverType;
      if (currentVideoCoverPreview) {
        coverType = VIDEO_COVER_TYPES.FRAME_SELECTED;
      } else if (
        currentThumbnailUrl &&
        currentTimestamp === VIDEO_COVER_TIMESTAMPS.UPLOADED_COVER
      ) {
        coverType = VIDEO_COVER_TYPES.UPLOADED;
      } else if (currentThumbnailUrl) {
        coverType = VIDEO_COVER_TYPES.PLATFORM_DEFAULT;
      } else {
        coverType = VIDEO_COVER_TYPES.FRAME_SELECTED;
      }

      updateFields(
        {
          [FIELDS.VIDEO_COVER_ORIGINAL]: thumb,
          [FIELDS.VIDEO_COVER_PREVIEW]: thumb,
          [FIELDS.VIDEO_COVER_TYPE]: coverType,
          [FIELDS.VIDEO_COVER_PREVIEW_SOURCE]: undefined,
        },
        selectedPlatform
      );

      // Store original values for revert functionality
      if (currentTimestamp !== undefined) {
        setOriginalValues({ timestamp: currentTimestamp, coverType });
      }
      logVideoCoverBreadcrumb(
        "video_cover.generate_original_cover.success",
        observabilityMetadata
      );
    } catch {
      if (operationId !== operationIdRef.current) return;
      logVideoCoverBreadcrumb(
        "video_cover.generate_original_cover.failure",
        observabilityMetadata
      );
      toast.error(
        "There was an issue selecting your cover, please try again or contact Support."
      );
    } finally {
      if (operationId === operationIdRef.current) {
        setIsLoadingThumb(false);
      }
    }
  }, [
    videoUrl,
    lowQualityMediaUrl,
    originalMediaUrl,
    platformFullname,
    // videoCoverPreview, thumbnailUrl, videoCoverImageTimestamp read via refs
    setCover,
    setIsLoadingThumb,
    setOriginalValues,
    updateFields,
    selectedPlatform,
  ]);

  return {
    onCoverChanged,
    onCoverUploaded,
    onRevertToOriginal,
    generateOriginalCover,
  };
}
